import { Link } from "react-router-dom";
import type { Game } from "@/types";
import { formatDate, getTeamById, stageLabel } from "@/utils/nba";
import TeamBadge from "@/components/base/TeamBadge";

interface GameCardProps {
  game: Game;
  variant?: "schedule" | "result";
}

export default function GameCard({ game, variant = "schedule" }: GameCardProps) {
  const home = getTeamById(game.homeTeamId);
  const away = getTeamById(game.awayTeamId);
  if (!home || !away) return null;

  const isLive = game.status === "live";
  const isFinal = game.status === "final";

  return (
    <Link
      to={`/detail/${game.id}`}
      className="group block rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 transition-all duration-300 hover:border-primary-300 hover:bg-primary-50/40 cursor-pointer"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
            {stageLabel(game.stage)}
          </span>
          {isLive && (
            <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-background-50">
              <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse-dot" />
              直播中
            </span>
          )}
          {isFinal && (
            <span className="rounded-md bg-background-200 px-2 py-0.5 text-xs font-medium text-foreground-600">
              已结束
            </span>
          )}
          {game.status === "upcoming" && (
            <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800">
              未开始
            </span>
          )}
        </div>
        <span className="text-xs text-foreground-500 whitespace-nowrap">
          {formatDate(game.date)} {game.time}
        </span>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <TeamBadge team={away} size="md" />
          <div className="min-w-0">
            <p className="text-sm md:text-base font-semibold text-foreground-950 truncate">{away.fullName}</p>
            <p className="text-xs text-foreground-500">客场</p>
          </div>
        </div>

        <div className="shrink-0 text-center px-2">
          {game.status === "upcoming" ? (
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-400">VS</span>
          ) : (
            <div className="flex items-center gap-2">
              <span className={`font-heading text-xl md:text-2xl font-bold ${isLive ? "text-accent-600" : "text-foreground-950"}`}>
                {game.awayScore}
              </span>
              <span className="text-foreground-300">-</span>
              <span className={`font-heading text-xl md:text-2xl font-bold ${isLive ? "text-accent-600" : "text-foreground-950"}`}>
                {game.homeScore}
              </span>
            </div>
          )}
          {isLive && game.clock && (
            <p className="text-[11px] text-accent-600 mt-0.5">{game.quarter} {game.clock}</p>
          )}
        </div>

        <div className="flex items-center gap-3 min-w-0 justify-end">
          <div className="text-right min-w-0">
            <p className="text-sm md:text-base font-semibold text-foreground-950 truncate">{home.fullName}</p>
            <p className="text-xs text-foreground-500">主场</p>
          </div>
          <TeamBadge team={home} size="md" />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
        <span className="text-xs text-foreground-500 truncate">{game.arena}</span>
        <span className="text-xs text-primary-600 font-medium whitespace-nowrap flex items-center gap-1 group-hover:gap-2 transition-all">
          {variant === "result" || isFinal ? "查看回放与战报" : "进入赛事详情"}
          <i className="ri-arrow-right-line" />
        </span>
      </div>
    </Link>
  );
}